import {
    FALLBACK_ROUTE,
    STEP_MAP,
    isInStepMap,
    useNavigateWorkflow,
} from '@data/hooks/registration/useNavigateWorkflow'
import {
    Data,
    RegistrationWorkflowNavigatorOptions,
    WorkflowStep,
    useRegistrationWorkflowNavigator,
} from '@data/hooks/registration/useRegistrationWorkflowNavigator'
import { setCurrentRegistrationUser } from '@data/store/slices/currentRegistrationUser/setters'
import { UseQueryResult } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'

const getNextStepRoute = (data: WorkflowData): TRoute => {
    const { canAccess, moveTo } = data

    if (canAccess || moveTo === null) {
        return FALLBACK_ROUTE
    }

    if (!isInStepMap(moveTo)) {
        return FALLBACK_ROUTE
    }

    return STEP_MAP[moveTo]
}

type Result = UseQueryResult<Data, ApiError> & {
    navigateWorkflow(workflow: WorkflowData): void
    getNextStepRoute(workflow: WorkflowData): TRoute
}

export const useRegistrationWorkflow = (
    step: WorkflowStep,
    options?: {
        blockRedirection?: boolean
        queryOptions?: RegistrationWorkflowNavigatorOptions
    }
): Result => {
    const queryResult = useRegistrationWorkflowNavigator(step, options?.queryOptions)
    const { data, status } = queryResult
    const navigateWorkflow = useNavigateWorkflow()

    // this should be moved into its own hook later, as it will be shared by all registration related
    // data hooks
    useEffect(() => {
        if (data && (status !== 'success' || !data.workflow.canAccess) && !options?.blockRedirection) {
            navigateWorkflow(data.workflow)
        }
    }, [options?.blockRedirection, data, status, navigateWorkflow])

    useEffect(() => {
        if (queryResult.data?.user) {
            setCurrentRegistrationUser(queryResult.data.user)
        }
    }, [queryResult.data?.user])

    const result: Result = useMemo(
        () => ({
            ...queryResult,
            navigateWorkflow,
            getNextStepRoute,
        }),
        [navigateWorkflow, queryResult]
    )

    return result
}
